import { Checkbox } from "@faire/slate/components/checkbox";
import { Tag } from "@faire/slate/components/tag";
import { getSlots } from "@faire/slate/lib/getSlots";
import { tsv } from "@faire/slate/lib/tsv";
import React from "react";

interface IProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "className"
> {
  /**
   * The label of the radio block.
   */
  label: string;

  /**
   * The optional description of the radio block.
   */
  description?: string;

  /**
   * The optional tag of the radio block.
   * @invariant tag is not allowed if variant is "multiselect"
   */
  tag?: React.ReactElement<React.ComponentProps<typeof Tag>, typeof Tag>;

  /**
   * The variant of the radio block.
   *
   * @default "default"
   */
  variant?: "default" | "multiselect";

  /**
   * **web**
   * The data-test-id of the radio block.
   */
  "data-test-id"?: string;

  /**
   * **web**
   * The id of the input element. Required for making the container link to the hidden input element.
   */
  id: string;
}

type RadioBlockConstraints =
  | {
      variant?: "default";
      tag?: IProps["tag"];
    }
  | {
      variant?: "multiselect";
      tag?: never;
    };

/**
 * {@link https://www.notion.so/faire/Radio-Block-2e82efb5c25a8002925bcca5cab92c6a | Community/RadioBlock}
 */
export const RadioBlock: React.FC<
  IProps & RadioBlockConstraints & React.RefAttributes<HTMLInputElement>
> = ({
  "data-test-id": dataTestId,
  id,
  label,
  description,
  tag,
  variant = "default",
  ...props
}) => {
  const isMultiselect = variant === "multiselect";

  const [{ tag: tagSlot }] = getSlots(tag, {
    tag: Tag,
  });

  // We do not allow onDismiss prop on tag, so we need to remove it from the tag element if it is provided.
  const tagElement = tagSlot
    ? React.cloneElement(tagSlot, { onDismiss: undefined })
    : undefined;

  return (
    <label
      htmlFor={id}
      className={labelStyle.cls()}
      data-test-id={dataTestId}
      data-component-name="Community/RadioBlock"
    >
      <div className="flex flex-1 flex-col self-center">
        <span className="type-fs-paragraph">{label}</span>
        {description ? (
          <span className="type-fs-label text-fs-text-subdued">
            {description}
          </span>
        ) : null}
      </div>
      {tagElement !== undefined && !isMultiselect ? (
        <div className="ml-(--slate-spacing-element-large) flex items-start">
          {tagElement}
        </div>
      ) : null}
      {/* For multiselect variant, the visible checkbox handles checked state, so we do not need the extra hidden input. Test id is kept the same for the sake of consistency */}
      {isMultiselect ? (
        <div className="ml-(--slate-spacing-element-large) flex items-center self-center">
          <Checkbox
            id={id}
            {...props}
            data-test-id={`hidden-input-${dataTestId}`}
          />
        </div>
      ) : (
        <input
          id={id}
          type="radio"
          {...props}
          data-test-id={`hidden-input-${dataTestId}`}
          className="sr-only"
        />
      )}
    </label>
  );
};

const labelStyle = tsv({
  base: [
    "px-6 py-5",
    "relative flex w-full items-start justify-between",
    "transition-[background-color,border-color] duration-300",
    "border-fs-action-border-subdued bg-fs-action-surface-inverse rounded-[8px] border",
    "hover:border-fs-action-border-hover",
    "interactive",
    "outline-offset-2 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-(--slate-color-action-border-focus_ring)",
    "has-[input:checked]:border-fs-action-border-active has-[input:checked]:bg-fs-action-surface-inverse-active",
  ],
});
